import { Callback1, VoidCallback, VoidCallback1 } from '@deqo/foundation';
import React, { useCallback, useState } from 'react';
import styled, { css } from 'styled-components';
import { Colors } from './Colors';
import { usePrompt } from './Prompt';
import { FixedScroller } from './Scrollers';
import { Styles } from './Styles';
import { toRem } from './Utils';

const Frame = styled.div<{ width?: number | string }>`
    position: relative;
    min-width: ${p => p.width ? toRem(p.width) : 'initial'};
`;

const InputBorder = styled.div`
    display: inline-block;
    width: 100%;
    border-radius: .275rem;
    ${Styles.shadowSm};
`;

const Input = styled.button<{ open?: boolean, error?: boolean }>`
    display: block;
    cursor: default;
    position: relative;
    width: 100%;
    min-width: 6rem;
    border-radius: .275rem;
    border: solid .0675rem ${p => p.error ? Colors.negative : Colors.gray300};
    background: white;
    padding-left: 0.75rem;
    padding-right: 2.5rem;
    text-align: left;
    outline: none;
    line-height: 2.4rem;
    cursor: pointer;

    &:hover {
        border-color: ${p => p.error ? Colors.negative : Colors.gray400};
    }

    &:focus {
        box-shadow: 0 0 0 .0675rem ${Colors.primary};
        border-color: ${Colors.primary};
    }

    ${p => p.open && css`
        border-color: ${Colors.gray400} !important;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        box-shadow: none !important;
    `}
    
`;

const InputText = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const InputPlaceholder = styled.span`
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    opacity: .5;
`;

const InputIconPlacement = styled.span`
    position: absolute;
    top: 0; 
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;
    padding-right: 0.5rem;
    pointer-events: none;
`;

const InputIconSvg = styled.svg`
    width: 1.25rem;
    height: 1.25rem;
    color: ${Colors.gray400};
`;

const InputIcon = () =>
    <InputIconSvg viewBox="0 0 20 20" fill="none" stroke="currentColor">
        <path d="M7 7l3-3 3 3m0 6l-3 3-3-3" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </InputIconSvg>;

const MenuFrame = styled.div`
    position: absolute;
    top: -.0625rem;
    left: -.0625rem;
    right: -.0625rem;
    padding-top: 2.4625rem;
    z-index: 50;
    border-radius: .275rem;
    box-shadow: 0 10px 15px -3px rgb(0 0 0 / 10%), 0 4px 6px -2px rgb(0 0 0 / 5%);
    border: solid .125rem ${Colors.primary};
    pointer-events: none;

    /* &:before {
        content: '';
        position: absolute;
        top: 0;
        left: .0625rem;
        right: .0625rem;
        bottom: 0;
        box-shadow: 0 0 0 .125rem ${Colors.primary};
        border-radius: .175rem;
        pointer-events: none;
    } */
`;

const MenuList = styled.ul`
    padding: 0.25rem 0;
    border-radius: 0.375rem;
    font-size: 1rem;
    line-height: 1.5rem;
    outline: 0;
    box-shadow: 0 1px 0 1px rgb(0 0 0 / 5%);
    background: white;
    pointer-events: all;
`;

const MenuItem = styled.li<{ disabled?: boolean }>`
    position: relative;
    padding: 0.5rem 2.25rem 0.5rem 0.75rem;
    color: ${Colors.gray900};
    user-select: none;
    cursor: pointer;
    background: white;

    &:hover {
        background: ${Colors.hover};
    }

    &:active {
        background: ${Colors.active};
    }

    ${p => !p.disabled ? '' : css`
        opacity: .4;
        background: none !important;
        cursor: not-allowed;
    `}
`;

// const MenuItem = sw.li({
//     base: ['text-gray-900', 'cursor-default', 'select-none', 'relative', 'py-2', 'pl-3', 'pr-9'],
//     css: `
//         &:hover { background: ${Colors.primaryHover}; }
//     `,
// });

const MenuItemText = styled.span`
    display: block;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const MenuItemCheckIconPlacement = styled.span`
    position: absolute;
    top: 0;
    bottom: 0;
    right: 0;
    display: flex;
    align-items: center;
    padding-right: 0.75rem;
    color: ${Colors.blue600};
`;

const MenuItemCheckIconSvg = styled.svg`
    width: 1.25rem;
    height: 1.25rem;
`;

const MenuItemCheckIcon = () =>
    <MenuItemCheckIconSvg viewBox="0 0 20 20" fill="currentColor">
        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
    </MenuItemCheckIconSvg>;

const MenuItemCheck = () =>
    <MenuItemCheckIconPlacement>
        <MenuItemCheckIcon />
    </MenuItemCheckIconPlacement>;

export interface SelectItemAdapter<T> {
    key(item: T): string;
    label(item: T): string;
}

export type SelectProps<T> = {
    width?: number | string,
    menuHeight?: number | string,
    adapter?: SelectItemAdapter<T>,
    items: T[],
    renderSelected?: (item: T | null) => React.ReactNode,
    renderItems?: (items: T[], selected: T | null, onSelect: Callback1<VoidCallback, T>) => React.ReactNode,
    selected?: T,
    tabIndex?: number,
    placeholder?: string,
    error?: boolean,
    onChange: VoidCallback1<T>,
};

const defaultSelectItemAdapter: SelectItemAdapter<any> = {
    key(item) {
        if (typeof (item) === 'object') {
            if ('key' in item) return item.name;
            if ('id' in item) return item.id;
            if ('value' in item) return item.value;
        }
        return item.toString();
    },
    label(item) {
        if (typeof (item) === 'object') {
            if ('label' in item) return item.label;
            if ('name' in item) return item.name;
            if ('text' in item) return item.text;
        }
        return item.toString();
    }
};

const Base = <T extends any>({ selected, onChange, ...rest }: SelectProps<T>) => {

    const inputElmt = React.createRef<HTMLButtonElement>();
    const [open, setOpen] = useState(false);

    const onInputClick = () => {
        setOpen(x => !x);
    };

    const onSelect = (tm: T) => () => {
        setOpen(false);
        onChange?.(tm);
        inputElmt?.current?.focus();
    };

    const { items, renderItems, renderSelected, error } = rest;
    const adapter = rest.adapter ?? defaultSelectItemAdapter;

    const promptRef = usePrompt({
        ignoreEnter: true,
        onAction: useCallback(() => setOpen(false), []),
    });

    return <Frame ref={promptRef} width={rest.width}>
        <InputBorder onClick={onInputClick}>
            <Input ref={inputElmt} open={open} error={error} tabIndex={rest.tabIndex ?? -1}>
                {!!renderSelected && renderSelected(selected)}
                {!renderSelected && (selected ? <InputText>{adapter.label(selected)}</InputText> : <InputPlaceholder>{rest.placeholder}&nbsp;</InputPlaceholder>)}
                <InputIconPlacement>
                    <InputIcon />
                </InputIconPlacement>
            </Input>
        </InputBorder>
        {open && <MenuFrame>
            <MenuList>
                <FixedScroller autoHide={false} maxHeight={toRem(rest.menuHeight || 15)}>
                    {!!renderItems && renderItems(items, selected, onSelect)}
                    {!renderItems && items.map((x, i) => <MenuItem key={adapter.key(x) ?? i} onClick={onSelect(x)}>
                        <MenuItemText>{adapter.label(x)}</MenuItemText>
                        {x === selected && <MenuItemCheck />}
                    </MenuItem>)}
                </FixedScroller>
            </MenuList>
        </MenuFrame>}
    </Frame>;
};


// class BaseX<T> extends React.Component<SelectProps<T>> {

//     public state = {
//         isOpen: false,
//         selected: null as T | null,
//     };

//     private inputElmt = React.createRef<HTMLButtonElement>();

//     public render() {

//     }

//     public componentDidMount() {
//         this.setState({ selected: this.props.selected });
//     }

//     public componentDidUpdate(pp: any) {
//         if (pp.selected !== this.props.selected && this.state.selected !== this.props.selected) {
//             this.setState({ selected: this.props.selected });
//         }
//     }


// }


export const Select = Object.assign(Base, {
    Frame,
    InputBorder,
    Input,
    InputText,
    InputPlaceholder,
    MenuFrame,
    MenuList,
    MenuItem,
    MenuItemText,
    MenuItemCheck,
    defaultAdapter: defaultSelectItemAdapter,
});
